import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { applyRuntimeAssets } from "../lib/data";

const SiteContext = createContext(null);
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const API = `${BACKEND_URL}/api`;

export const DEFAULT_SITE_SETTINGS = {
  identity: { site_name: "TowerLink", site_suffix: "Platform", logo_url: "", favicon_url: "", tagline_ar: "", tagline_en: "" },
  theme: {
    primary: "#D4AF37", primary_hover: "#B5952F", background: "#0A1223", surface: "#11203A",
    surface_alt: "#1A2C47", text_primary: "#F8FAFC", text_secondary: "#AAB6C6",
    body_font: "IBM Plex Sans", heading_font: "Cabinet Grotesk",
  },
  contact: { email: "rfq@towerlinkgcc.com", phone: "", whatsapp: "966549747549", address_ar: "", address_en: "" },
  social: {}, assets: {}, navigation: [], floating_action: { enabled: true, type: "whatsapp", label_ar: "طلب عاجل", label_en: "Send Urgent Request" }, seo: {}, analytics: { ga4_id: "" },
};

const deepMerge = (base, extra) => {
  if (!extra || typeof extra !== "object" || Array.isArray(extra)) return extra === undefined ? base : extra;
  const out = { ...(base || {}) };
  Object.entries(extra).forEach(([key, value]) => {
    out[key] = value && typeof value === "object" && !Array.isArray(value) ? deepMerge(out[key] || {}, value) : value;
  });
  return out;
};

export const resolveAssetUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url) || url.startsWith("data:") || url.startsWith("blob:")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

export const SiteProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULT_SITE_SETTINGS);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const [settingsRes, pagesRes] = await Promise.all([
        axios.get(`${API}/site/settings`),
        axios.get(`${API}/site/pages`),
      ]);
      setSettings(deepMerge(DEFAULT_SITE_SETTINGS, settingsRes.data || {}));
      setPages(pagesRes.data || []);
    } catch (e) {
      // Keep safe defaults if the CMS API is temporarily unavailable.
      console.warn("CMS settings unavailable; using defaults", e?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    const t = settings.theme || {};
    const root = document.documentElement;
    const map = {
      "--gold": t.primary,
      "--gold-hover": t.primary_hover,
      "--navy-900": t.background,
      "--navy-800": t.surface,
      "--navy-700": t.surface_alt,
      "--text-primary": t.text_primary,
      "--text-secondary": t.text_secondary,
      "--font-body": t.body_font ? `'${t.body_font}', sans-serif` : undefined,
      "--font-heading": t.heading_font ? `'${t.heading_font}', sans-serif` : undefined,
    };
    Object.entries(map).forEach(([k, v]) => v && root.style.setProperty(k, v));
  }, [settings]);

  useEffect(() => {
    const resolvedAssets = Object.fromEntries(Object.entries(settings?.assets || {}).filter(([, v]) => v).map(([k, v]) => [k, resolveAssetUrl(v)]));
    window.__TL_ASSETS__ = resolvedAssets;
    applyRuntimeAssets(resolvedAssets);
  }, [settings]);


  useEffect(() => {
    const id = String(settings?.analytics?.ga4_id || "").trim();
    const old = document.getElementById("towerlink-ga4");
    if (!id) {
      old?.remove();
      return;
    }
    if (old?.dataset?.gaId === id) return;
    old?.remove();
    const script = document.createElement("script");
    script.id = "towerlink-ga4";
    script.async = true;
    script.dataset.gaId = id;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(script);
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag(){ window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", id);
  }, [settings]);

  useEffect(() => {
    const favicon = resolveAssetUrl(settings?.identity?.favicon_url);
    if (!favicon) return;
    let link = document.querySelector("link[rel='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = favicon;
  }, [settings]);

  const pagesBySlug = useMemo(() => Object.fromEntries((pages || []).map((p) => [p.slug, p])), [pages]);
  const value = useMemo(() => ({ settings, pages, pagesBySlug, loading, refresh, assetUrl: resolveAssetUrl }), [settings, pages, pagesBySlug, loading]);
  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
};

export const useSite = () => {
  const ctx = useContext(SiteContext);
  if (!ctx) throw new Error("useSite must be used within SiteProvider");
  return ctx;
};
